/*
 * Modified from https://github.com/backstage/backstage/plugins/catalog-react/src/components/EntityKindPicker/EntityKindPicker.tsx
 *
 * ORIGINAL LICENSE:
 * -- Copyright 2020 The Backstage Authors
 * --
 * -- Licensed under the Apache License, Version 2.0 (the "License");
 * -- you may not use this file except in compliance with the License.
 * -- You may obtain a copy of the License at
 * --
 * --     http://www.apache.org/licenses/LICENSE-2.0
 * --
 * -- Unless required by applicable law or agreed to in writing, software
 * -- distributed under the License is distributed on an "AS IS" BASIS,
 * -- WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * -- See the License for the specific language governing permissions and
 * -- limitations under the License.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import Box from '@material-ui/core/Box';
import { Select, SelectItem, SelectedItems } from '@backstage/core-components';
import {
  EntityKindFilter,
  EntityTypeFilter,
  useEntityList,
} from '@backstage/plugin-catalog-react';

/**
 * Props for {@link MedidataKindPicker}.
 *
 * @public
 */
export interface MedidataKindPickerProps {
  /**
   * Entity kinds/types to show in the dropdown
   */
  initialFilter?: string;
  hidden?: boolean;
}

/** @public */
export const MedidataKindPicker = (props: MedidataKindPickerProps) => {
  const { hidden, initialFilter = 'component' } = props;

  const {
    filters,
    queryParameters: { kind: kindParameter, type: typeParameter },
    updateFilters,
  } = useEntityList();

  const stringifyCategory = useCallback(
    (kindParam?: string, typeParam?: string): string => {
      if (kindParam === 'group' && typeParam) {
        return `${kindParam}/${typeParam}`;
      }
      return kindParam ?? initialFilter;
    },
    [initialFilter],
  );

  const parseCategory = (
    category: string,
  ): { kind: EntityKindFilter; type?: EntityTypeFilter } => {
    const catParts = category.split('/');
    return {
      kind: new EntityKindFilter(catParts[0], catParts[0]),
      type: catParts.length > 1 ? new EntityTypeFilter(catParts[1]) : undefined,
    };
  };

  const queryParamKind = useMemo(
    () => [kindParameter].flat()[0],
    [kindParameter],
  );
  const queryParamType = useMemo(
    () => [typeParameter].flat()[0],
    [typeParameter],
  );
  const filterType = useMemo(
    () => [filters.type?.value].flat()[0],
    [filters.type],
  );

  const [selectedCategory, setSelectedCategory] = useState<string>(
    stringifyCategory(
      queryParamKind ?? filters.kind?.value,
      queryParamType ?? filterType,
    ),
  );

  useEffect(() => {
    if (queryParamKind) {
      setSelectedCategory(stringifyCategory(queryParamKind, queryParamType));
    }
  }, [queryParamKind, queryParamType, stringifyCategory]);

  useEffect(() => {
    if (filters.kind?.value) {
      setSelectedCategory(stringifyCategory(filters.kind?.value, filterType));
    }
  }, [filters.kind, filterType, stringifyCategory]);

  useEffect(() => {
    updateFilters(parseCategory(selectedCategory));
  }, [selectedCategory, updateFilters]);

  const items: SelectItem[] = [
    {
      value: 'api',
      label: 'API',
    },
    {
      value: 'component',
      label: 'Component',
    },
    {
      value: 'system',
      label: 'Product',
    },
    {
      value: 'domain',
      label: 'Product Family',
    },
    {
      value: 'group/solution-line',
      label: 'Organization',
    },
    {
      value: 'group/organization',
      label: 'Company',
    },
    {
      value: 'group/team',
      label: 'Team',
    },
    {
      value: 'user',
      label: 'User',
    },
  ];

  const handleChange = useCallback((value: SelectedItems) => {
    setSelectedCategory(value.toString());
  }, []);

  return hidden ? null : (
    <Box pb={1} pt={1}>
      <Select
        label="Kind"
        items={items}
        selected={selectedCategory}
        onChange={handleChange}
      />
    </Box>
  );
};
